// Controller + bootstrap.
//
// Wires the DOM to the state: IndexedDB is the single source of truth for
// notes, the roke/carto selection is ephemeral UI state on the toggle
// element, and `render` repaints the view as a pure function of state.

import type { EmailDest, Note } from "./types.js";
import { $ } from "./dom.js";
import { getAllNotes, getNote, putNote } from "./db.js";
import { emailSubject, newId } from "./format.js";
import { asError, asPending, asSent } from "./note.js";
import { viewNodes } from "./view.js";

// Public anti-bot token sent with every send request. NOT a secret — it ships
// in the client bundle and only filters anonymous/automated POSTs. The server
// enforces it via the APP_TOKEN env var; keep the two values in sync.
const APP_TOKEN = "n2m_5d030c69111e0160cb84bffa";

// --- DOM refs ------------------------------------------------------------

const messagesEl = $<HTMLDivElement>("#messages");
const inputEl = $<HTMLTextAreaElement>("#input");
const sendBtn = $<HTMLButtonElement>("#send");
const destToggle = $<HTMLButtonElement>("#dest-toggle");

// --- Render (effect: read state, paint the DOM) --------------------------

const render = async (): Promise<void> => {
  const notes = await getAllNotes();
  messagesEl.replaceChildren(...viewNodes(notes, (id) => void trySend(id)));
  messagesEl.scrollTop = messagesEl.scrollHeight;
};

// --- Dest selection: ephemeral UI state on the toggle element -------------

const getDest = (): EmailDest =>
  destToggle.dataset.dest === "carto" ? "carto" : "roke";

const setDest = (dest: EmailDest): void => {
  destToggle.dataset.dest = dest;
  destToggle.textContent = dest;
};

// --- Sending -------------------------------------------------------------

// Attempts to send; returns the resulting note without mutating the input.
//   ok         → sent
//   http error → error (with detail)
//   network    → pending (offline, manual retry)
const attemptSend = async (note: Note): Promise<Note> => {
  try {
    const res = await fetch("/api/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-App-Token": APP_TOKEN,
      },
      body: JSON.stringify({
        text: note.text,
        dest: note.dest,
        subject: emailSubject(note.text),
      }),
    });

    if (res.ok) return asSent(note);

    const detail = await res.text().catch(() => "");
    return asError(note, `Erreur ${res.status} : ${detail || res.statusText}`);
  } catch {
    return asPending(note);
  }
};

const persistAndRender = async (note: Note): Promise<void> => {
  await putNote(note);
  await render();
};

const trySend = async (id: string): Promise<void> => {
  const existing = await getNote(id);
  if (!existing || existing.status === "sent") return;

  const attempting = asPending(existing);
  await persistAndRender(attempting);
  await persistAndRender(await attemptSend(attempting));
};

const createAndSend = async (text: string): Promise<void> => {
  const note: Note = {
    id: newId(),
    text,
    timestamp: Date.now(),
    dest: getDest(),
    status: "pending",
    errorMessage: null,
  };
  await persistAndRender(note);
  await trySend(note.id);
};

// --- Events --------------------------------------------------------------

const autoGrow = (): void => {
  inputEl.style.height = "auto";
  inputEl.style.height = `${Math.min(inputEl.scrollHeight, 160)}px`;
};

const onSend = (): void => {
  const text = inputEl.value.trim();
  if (!text) return;
  inputEl.value = "";
  autoGrow();
  void createAndSend(text);
};

const wireEvents = (): void => {
  sendBtn.addEventListener("click", onSend);

  inputEl.addEventListener("input", autoGrow);
  inputEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  });

  destToggle.addEventListener("click", () =>
    setDest(getDest() === "roke" ? "carto" : "roke"),
  );
};

// --- Bootstrap -----------------------------------------------------------

const main = async (): Promise<void> => {
  setDest("roke");
  wireEvents();
  await render();

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Offline PWA support is optional — ignore if unavailable.
    });
  }
};

void main();
